// Mock data for the AlertRunDetail page so we can iterate on UX without
// the backend wired up. URL param `?state=<key>` selects a state.
// Once the runs collection lands, replace `getMockRun(state)` with a real
// fetch and delete this file.

const baseProperty = {
	property_id: "p-1-200-e-83rd-st-apt-16c-10028",
	address: "200 East 83rd Street",
	unit: "Apt 16C",
	neighborhood: "Yorkville",
	borough: "Manhattan",
	bedrooms: 2,
	baths: 2,
	sq_ft: 1100,
	price: 1250000,
	off_market_type: "expired",
	off_market_date: "2026-04-22T00:00:00Z",
	building_class: "R4",
	days_on_market: 184,
	url: "https://pro.marketproof.com/buildings/manhattan/yorkville/200-east-83rd-street-10028/apt-16c"
};

function makeProperty(i, overrides = {}) {
	const p = Object.assign({}, baseProperty, overrides);
	return Object.assign(p, {
		property_id: `p-1-mock-${i}-10028`,
		address: overrides.address || `${100 + i * 13} Mock Street`,
		unit: overrides.unit || (i % 4 === 0 ? "" : `Apt ${4 + i}${["A","B","C","D"][i % 4]}`),
		bedrooms: overrides.bedrooms !== undefined ? overrides.bedrooms : ((i % 4) + 1),
		baths: overrides.baths !== undefined ? overrides.baths : ((i % 3) + 1),
		sq_ft: overrides.sq_ft || 600 + i * 75,
		price: overrides.price || 850000 + i * 47000,
		off_market_type: overrides.off_market_type || (i % 3 === 0 ? "pom" : "expired"),
		off_market_date: overrides.off_market_date || `2026-04-${String(20 - (i % 7)).padStart(2, "0")}T00:00:00Z`,
		days_on_market: overrides.days_on_market || (60 + i * 17),
		url: `https://pro.marketproof.com/buildings/manhattan/yorkville/mock-${i}`
	});
}

const sampleContacts = [
	{ owner_name: "Jane Smith", phones: ["(212) 555-0123", "(917) 555-9911"], emails: ["jane@example.com"], mailing_address: "200 E 83rd St Apt 16C, New York, NY 10028" },
	{ owner_name: "Robert Chen", phones: ["(646) 555-7733"], emails: ["rchen@example.com", "robert.chen@workmail.com"], mailing_address: "150 W 56th St, New York, NY 10019" },
	{ owner_name: "Maria Gonzalez", phones: [], emails: ["maria.g@example.com"], mailing_address: "" },
	{ owner_name: "David Kim", phones: ["(718) 555-1212"], emails: [], mailing_address: "" },
	{ owner_name: "Sarah O'Brien", phones: ["(212) 555-4488"], emails: ["sobrien@example.com"], mailing_address: "100 Riverside Dr, New York, NY 10024" }
];

function attachContacts(properties, mode) {
	return properties.map((p, i) => {
		if (mode === "skipped") return Object.assign({}, p, { skip_trace_status: "off" });
		if (mode === "loading") {
			const ready = i < 3;
			return Object.assign({}, p, {
				skip_trace_status: ready ? "complete" : "pending",
				contact: ready ? sampleContacts[i % sampleContacts.length] : null
			});
		}
		if (mode === "partial") {
			if (i % 5 === 0) return Object.assign({}, p, { skip_trace_status: "no_match", contact: null });
			return Object.assign({}, p, {
				skip_trace_status: "complete",
				contact: sampleContacts[i % sampleContacts.length]
			});
		}
		// complete (default)
		return Object.assign({}, p, {
			skip_trace_status: "complete",
			contact: sampleContacts[i % sampleContacts.length]
		});
	});
}

export function getMockRun(state) {
	const knownStates = ["complete", "partial", "skipped", "loading", "empty", "failed", "no_credit", "no_credit_autorecharge"];
	const s = knownStates.indexOf(state) >= 0 ? state : "complete";

	const subscription = {
		_id: "sub-mock-1",
		name: "Off-market alert: Manhattan · Condo · 2 BR",
		userEmail: "ning@marketproof.com",
		offMarketTypes: ["expired", "pom"],
		maxResults: 25,
		searchTags: [{ type: "borough", data: "manhattan" }],
		filters: { ownership_type: ["condo"], bedrooms: ["2-bedroom"] }
	};

	const baseRun = {
		_id: "run-mock-1",
		subscriptionId: "sub-mock-1",
		runAt: "2026-04-30T11:00:00Z",
		sinceDate: "2026-04-29T11:00:00Z",
		untilDate: "2026-04-30T11:00:00Z",
		matchCount: 25,
		sentCount: 25,
		skipTraceEnabled: s !== "skipped",
		skipTraceStatus: s,
		creditsUsed: s === "complete" ? 21 : s === "partial" ? 19 : s === "loading" ? 3 : 0,
		cacheHits: s === "complete" ? 4 : s === "partial" ? 3 : 0,
		csvUrl: s === "complete" || s === "partial" ? "/expired-listings/runs/run-mock-1/download" : null
	};

	if (s === "empty") {
		return {
			subscription: subscription,
			run: Object.assign({}, baseRun, { matchCount: 0, sentCount: 0, properties: [], skipTraceStatus: "skipped" })
		};
	}
	if (s === "failed") {
		return {
			subscription: subscription,
			run: Object.assign({}, baseRun, {
				skipTraceStatus: "failed",
				skipTraceError: "Insufficient Premium Credits and auto-recharge failed",
				properties: attachContacts(Array.from({ length: 12 }).map((_, i) => makeProperty(i)), "skipped")
			})
		};
	}
	// Partial trace where balance ran out mid-run. Two flavors so we can preview
	// both banner variants: with auto-recharge on (points to "check your card")
	// and without (points to "top up or enable auto-recharge").
	if (s === "no_credit" || s === "no_credit_autorecharge") {
		const noCreditProps = Array.from({ length: 8 }).map((_, i) => makeProperty(i));
		const traced = attachContacts(noCreditProps.slice(0, 3), "partial");
		const skipped = noCreditProps.slice(3).map(p => Object.assign({}, p, { skip_trace_status: "no_credit", owner: "Owner Name " + (p.property_id || "") }));
		return {
			subscription: subscription,
			run: Object.assign({}, baseRun, {
				matchCount: 12,
				sentCount: 8,
				skipTraceStatus: "no_credit",
				skipTraceStoppedReason: "out_of_credits",
				autoRechargeEnabledAtRun: s === "no_credit_autorecharge",
				properties: traced.concat(skipped)
			})
		};
	}

	const props = Array.from({ length: 12 }).map((_, i) => makeProperty(i));
	return {
		subscription: subscription,
		run: Object.assign({}, baseRun, {
			matchCount: 12,
			sentCount: 12,
			properties: attachContacts(props, s)
		})
	};
}
